<template>
  <div class="staff-requests-overview">
    <div class="staff-requests-overview__title">
      <h2>Stipend Requests</h2>
    </div>
    <div class="staff-requests-overview__inner">
      <div class="staff-requests-overview__filters">
        <div v-if="isStaff || isTcrAdmin" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Date</h5>

          <md-field>
            <label for="selectedDateField">Choose a date field</label>
            <md-select
              @input="dateFieldChanged"
              :value="selectedDateField"
              class="box"
            >
              <md-option value="datetime_created">Date created</md-option>
              <md-option value="follow_up_date">Follow-up</md-option>
              <md-option value="submitted_on">Submitted on</md-option>
              <md-option value="closed_on">Closed on</md-option>
            </md-select>
          </md-field>

          <md-field class="DateRange">
            <label for="selectedDateRange">Choose a date range</label>
            <md-select
              @input="dateRangeChanged"
              :value="selectedDateRange"
              class="box"
            >
              <md-option value="all_time">All time</md-option>
              <md-option value="year_to_date">Year to Date</md-option>
              <md-option value="custom">Custom Date Range</md-option>
            </md-select>
          </md-field>


          <div
            v-if="isCustomDatePickerVisible"
            class="Report__Filter__DateRange"
          >
            <md-field
              class="md-override Report__Filter__DateRange-field"
              md-inline
            >
              <md-datepicker
                class="md-override Report__Filter__DateRange-picker"
                v-model="selectedStartDate"
                md-immediately
              >
                <label>Start date</label>
              </md-datepicker>
            </md-field>

            <md-field
              class="md-override Report__Filter__DateRange-field"
              md-inline
            >
              <md-datepicker
                class="md-override Report__Filter__DateRange-picker"
                v-model="selectedEndDate"
                md-immediately
              >
                <label>End date</label>
              </md-datepicker>
            </md-field>
          </div>
        </div>

        <!-- Organization filter -->
        <div v-if="isStaff" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Organization</h5>
          <md-field>
            <label for="status">Organization</label>
            <md-select
              name="status"
              id="status"
              @md-selected="refresh"
              v-model="stipend_requests.filters.organizations"
              multiple
            >
              <md-option
                v-for="status in StipendRequest.options.STIPEND_REQUEST_ORGANIZATION_CHOICES"
                :value="status.value"
                :key="status.value"
              >
                {{ status.label }}
              </md-option>
            </md-select>
            <div
              @click="clearArrayFilter('organizations')"
              class="icon-container"
              v-if="hasArrayFilter('organizations')"
            >
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>

        <div v-if="isStaff || isTcrAdmin" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Status</h5>
          <md-field>
            <label for="status">Status</label>
            <md-select
              name="status"
              id="status"
              @md-selected="refresh"
              v-model="stipend_requests.filters.statuses"
              multiple
            >
              <md-option
                v-for="status in StipendRequest.options.REQUEST_STATUS_CHOICES"
                :value="status.value"
                :key="status.value"
              >
                {{ status.label }}
              </md-option>
            </md-select>
            <div
              @click="clearArrayFilter('statuses')"
              class="icon-container"
              v-if="hasArrayFilter('statuses')"
            >
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>

        <div v-if="isStaff || isTcrAdmin" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Program Month</h5>
          <md-field>
            <label for="programMonth">Program Month</label>
            <md-select
              name="programMonth"
              id="programMonth"
              @md-selected="refresh"
              v-model="stipend_requests.filters.programMonths"
              multiple
            >
              <md-option
                v-for="programMonth in StipendRequest.options.PROGRAM_MONTH_LIST"
                :value="programMonth"
                :key="programMonth"
              >
                {{ programMonth }}
              </md-option>
            </md-select>
            <div
              @click="clearArrayFilter('programMonths')"
              class="icon-container"
              v-if="hasArrayFilter('programMonths')"
            >
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>
        <div v-if="isStaff" class="FilterAspect">
          <h5 class="FilterAspect__Title">Filter by Assigned</h5>
          <md-field>
            <label for="assigned">Staff Member</label>
            <md-select
              name="assigned"
              id="assigned"
              @md-selected="refresh"
              v-model="stipend_requests.filters.assignedTo"
            >
              <md-option v-for="staff in staff.list" :value="staff.id" :key="staff.id">
                {{ (staff.firstName && staff.lastName) ? staff.firstName + ' ' + staff.lastName : staff.email || $gettext('Unnamed User') }}
              </md-option>
              <md-option value="None" key="none"> Unassigned </md-option>
            </md-select>
            <div @click="resetAssigned" class="icon-container" v-if="stipend_requests.filters.assignedTo">
              <md-icon class="FilterAspect__Clear">close</md-icon>
            </div>
          </md-field>
        </div>
      </div>

      <div class="staff-requests-overview__table">
        <TableHeader :collection="stipend_requests" />
        <LoadSpinner v-if="stipend_requests.refreshing" style="padding: 2rem 0" />
        <div class="table-wrapper">
          <md-table>
            <md-table-row class="header-row">
              <md-table-head>
                Name
                <span class="sort-icon" @click="onSortByField('user__last_name')">
                  <md-icon v-if="sorts.user__last_name === null">sort</md-icon>
                  <md-icon v-else-if="sorts.user__last_name === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head v-show="false" v-if="organizationDetails.subOrganizations.length">
                Sub Organization
                <span class="sort-icon" @click="onSortByField('user__sub_organization__name')">
                  <md-icon v-if="sorts.user__sub_organization__name === null">sort</md-icon>
                  <md-icon v-else-if="sorts.user__sub_organization__name === ASC">
                    arrow_drop_down
                  </md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head>
                Date Submitted
                <span class="sort-icon" @click="onSortByField('submitted_on')">
                  <md-icon v-if="sorts.submitted_on === null">sort</md-icon>
                  <md-icon v-else-if="sorts.submitted_on === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head>
                Program Month
                <span class="sort-icon" @click="onSortByField('program_month')">
                  <md-icon v-if="sorts.program_month === null">sort</md-icon>
                  <md-icon v-else-if="sorts.program_month === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head> Status </md-table-head>
              <md-table-head>
                Follow-Up
                <span class="sort-icon" @click="onSortByField('follow_up_date')">
                  <md-icon v-if="sorts.follow_up_date === null">sort</md-icon>
                  <md-icon v-else-if="sorts.follow_up_date === ASC">arrow_drop_down</md-icon>
                  <md-icon v-else>arrow_drop_up</md-icon>
                </span>
              </md-table-head>
              <md-table-head>
                Last Communication
              </md-table-head>
            </md-table-row>

            <template v-for="request in stipend_requests.list">
              <md-table-row
                class="body-row"
                :class="{ 'gray-row': request.employeeInactive }"
                @click.native="() => onRowClick(request)"
              >
                <md-table-cell>{{
                  request.fullName || $gettext('Unnamed User')
                }}</md-table-cell>
                <md-table-cell v-show="false">{{
                    request.subOrganizationName ||
                    'Unnamed Sub Org.'
                }}</md-table-cell>
                <md-table-cell>{{
                  request.submittedOn | formatDateShort($language.current)
                }}</md-table-cell>
                <md-table-cell>{{ request.programMonth }}</md-table-cell>
                <md-table-cell v-if="request.status !== 'SUBMITTED'">{{
                  request.status
                }}</md-table-cell>
                <md-table-cell v-else>{{ request.submittedDisplay }}</md-table-cell>
                <md-table-cell>{{ request.followUpDate | datePickerFormat }}</md-table-cell>
                <md-table-cell>
                <div class="last-communicated">
                  <div v-if="request.lastUserMessagedAt" class="user">
                    Applicant: {{ request.lastUserMessagedAt | formatDateShort($language.current) }}
                    <div>
                      {{ dateToWords(request.lastUserMessagedAt) }}
                    </div>
                  </div>
                  <div v-if="request.lastAdminMessagedAt" class="admin">
                    Admin: {{ request.lastAdminMessagedAt | formatDateShort($language.current) }}
                    <div>
                      {{ dateToWords(request.lastAdminMessagedAt) }}
                    </div>
                  </div>
                </div>
              </md-table-cell>
              </md-table-row>
            </template>
          </md-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { LoadSpinner, TableHeader } from '@/components'
import { mapState } from 'vuex'
import NominationsApi from '@/services/nominations'
import Organization from '@/services/organizations'
import SelectOrganization from '@/services/selectOrganizations'
import CollectionManager from '@/services/collectionManager'
import AdminPermissions from '@/services/adminPermissions'
import StipendRequest from '@/services/stipendRequests'
import Staff from '@/services/staff'
import Pagination from '@/services/pagination'
import { DateTime } from 'luxon'

const REFERRED_ONLY = 'REFERRED_ONLY'
const NON_REFERRED_ONLY = 'NON_REFERRED_ONLY'
const ASC = 'ASC'
const DESC = 'DESC'

export default {
  name: 'StipendRequestsOverview',
  components: {
    LoadSpinner,
    TableHeader,
  },
  data: function() {
    return {
      StipendRequest,
      REFERRED_ONLY,
      NON_REFERRED_ONLY,
      selectedDateRange: "all_time",
      isCustomDatePickerVisible: false,
      selectedDateField: "datetime_created",
      selectedStartDate: null, // forces md-datepicker to Date model
      selectedEndDate: null, // forces md-datepicker to Date model
      reportFilter: null,
      selectedReferred: null,
      stipend_requests: CollectionManager.create({
        ModelClass: StipendRequest,
        pagination: Pagination.create({ pageSize: 50 }),
        filters: {
          ordering: '-datetime_created',
          statuses: [],
          organizations: [],
          referredOnly: null,
          nonReferredOnly: false,
          submittedStatuses: [],
          assignedTo: '',
          subOrganizations: [],
          programMonths: [],
          startDate: null,
          endDate: null,
          organization: null,
          dateField: 'datetime_created',
        },
      }),
      organizations: CollectionManager.create({
        ModelClass: SelectOrganization,
        pagination: Pagination.create({ pageSize: 100 }),
      }),
      staff: CollectionManager.create({
        ModelClass: Staff,
      }),
      sorts: {
        user__last_name: null,
        submitted_on: null,
        follow_up_date: null,
        program_month: null,
      },
      ASC,
      DESC,
    }
  },
  async created() {
    this.refresh()
    this.organizations.refresh()
    this.stipend_requests.refresh()
    this.staff.refresh()
  },
  methods: {
    async refresh() {
      this.nominations = await NominationsApi.list()
      if(this.reportFilter) {
        this.stipend_requests.filters['startDate'] = this.reportFilter.startDate
        this.stipend_requests.filters['endDate'] = this.reportFilter.endDate
      }

      this.stipend_requests.refresh()
    },
    onSortByField(field) {
      // toggle ascending / descending, while resetting other sorts (currently only filtering by one at a time)
      let ascending = this.sorts[field] === DESC || this.sorts[field] == null
      let newSortState = {
        user__last_name: null,
        submitted_on: null,
        follow_up_date: null,
        program_month: null,
      }
      newSortState[field] = ascending ? ASC : DESC
      this.sorts = newSortState

      let term = `${ascending ? '' : '-'}${field}`
      // if sorting by name, include first_name as second sorting criteria
      if (field == 'user__last_name') {
        term += `,${ascending ? '' : '-'}${'user__first_name'}`
      }
      this.updateRequestSort({ ordering: term })
    },
    updateRequestSort(filtersObj = {}) {
      this.stipend_requests = CollectionManager.create({
        ModelClass: StipendRequest,
        filters: {
          ...this.stipend_requests.filters,
          ...filtersObj,
        },
      })
      this.refresh()
    },
    onRowClick(request) {
      const routeData = this.$router.resolve({
        name: 'StaffStipendRequestDetail',
        params: {
          id: request.id,
        },
      })

      window.open(routeData.href, '_blank')
    },
    hasArrayFilter(filterKey) {
      let container = this.stipend_requests.filters[filterKey]
      return container ? container.length > 0 : false
    },
    clearArrayFilter(filterKey) {
      this.stipend_requests.filters[filterKey] = []
    },
    dateToWords(date) {
      return DateTime.fromJSDate(date).toRelativeCalendar()
    },
    dateFieldChanged(value) {
      this.selectedDateField = value
      this.stipend_requests.filters.dateField = value
      this.stipend_requests.refresh()
    },
    dateRangeChanged(value) {
      if (value == this.selectedDateRange) { return; }

      this.selectedDateRange = value

      switch (this.selectedDateRange) {
        case "all_time":
          this.isCustomDatePickerVisible = false
          this.setReportFilter(null)
          break

        case "year_to_date":
          this.isCustomDatePickerVisible = false
          const now = new Date(Date.now())
          const thisYear = new Date(Date.now())

          thisYear.setFullYear(new Date().getFullYear(), 0, 1)

          const filter = {
            startDate: thisYear,
            endDate: now
          }

          this.setReportFilter(filter)
          break

        case "custom":
          this.isCustomDatePickerVisible = true
          this.buildCustomDateRangeFilter()
          break
      }
    },
    buildCustomDateRangeFilter() {
      const start = this.selectedStartDate
      const end = this.selectedEndDate

      if (!start || !end) {
        return
      }

      const startTimestamp = Date.parse(start)
      const endTimestamp = Date.parse(end)
      if (isNaN(startTimestamp) || isNaN(endTimestamp)) {
        return
      }

      const startDate = new Date(startTimestamp)
      const endDate = new Date(endTimestamp)

      this.setReportFilter({ startDate, endDate })
    },
    dateValueChanged(newValue, oldValue) {
      // Work around md-datepicker giving us a string instead of a Date
      const newTimestamp = newValue ? Date.parse(newValue) : null
      const oldTimestamp = oldValue ? Date.parse(oldValue) : null
      if (isNaN(newTimestamp) || isNaN(oldTimestamp)) { return }

      // Work around md-datepicker firing two identical events for a single
      // change event.
      // See https://github.com/vuematerial/vue-material/issues/2103
      if (oldTimestamp == newTimestamp) { return }

      const newDate = new Date(newTimestamp)
      const oldDate = new Date(oldTimestamp)

      // Work around md-datepicker triggering two watch events with different
      // timezone data for the same change event
      if (
        oldTimestamp && newTimestamp &&
        (
          (new Date(oldTimestamp)).toDateString() == (new Date(newTimestamp)).toDateString()
        )
      ) {
        return
      }

      this.buildCustomDateRangeFilter()
    },
    setReportFilter(filter) {
      // Setting null value clears the filter
      if (!filter) {
        this.selectedStartDate = null
        this.stipend_requests.filters.startDate = null
        this.selectedEndDate = null
        this.stipend_requests.filters.endDate = null
        this.stipend_requests.filters.dateField = this.selectedDateField
        this.stipend_requests.refresh()
        return
      }

      // Ignore incomplete filter
      if (!filter.startDate || !filter.endDate) {
        return
      }

      // Ignore invalid filter
      if (filter.startDate > filter.endDate) {
        // @TODO: set validation error in UI
        return
      }

      // Ignore new filter of same value as existing
      if (
        this.reportFilter &&
        filter.startDate == this.reportFilter.startDate &&
        filter.endDate == this.reportFilter.endDate
      ) {
        return
      }

      this.stipend_requests.filters.startDate = filter.startDate
      this.stipend_requests.filters.endDate = filter.endDate
      this.stipend_requests.filters.dateField = this.selectedDateField
      this.stipend_requests.refresh()
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    showSubOrgFilter() {
      return this.organizationDetails.subOrganizations
    },
    showReferredColumn() {
      if (
        this.user.isAdmin &&
        this.user.adminPermissions.name === AdminPermissions.permissionNames.READ_ONLY
      ) {
        return false
      }

      return true
    },
    isStaff() {
      if(!this.user)
        return false
      
      return this.user.isStaff
    },
    isTcrAdmin() {
      return this.user.isAdmin && this.user.adminPermissions.name == AdminPermissions.permissionNames.TCR_ADMIN
    },
    /* Don't show "referred" filter if user is admin who can only see referred requests anyway */
    showReferredFilter() {
      if (
        (this.user.isAdmin &&
          this.user.adminPermissions.viewWhichRequests ===
            AdminPermissions.options.REFERRED_REQUESTS_ONLY) ||
        (this.user.isAdmin &&
          this.user.adminPermissions.name === AdminPermissions.permissionNames.READ_ONLY)
      ) {
        return false
      }
      return true
    },
  },
  watch: {
    selectedStartDate(newStart, oldStart) {
      this.dateValueChanged(newStart, oldStart);
    },
    selectedEndDate(newEnd, oldEnd) {
      this.dateValueChanged(newEnd, oldEnd);
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';
@import '../../styles/mixins/containers';

.staff-requests-overview {
  @include screen-container($mobile-padding: 1rem 0.5rem, $desktop-padding: 1rem 0.5rem, $desktop-max-width: 112rem);

  &__title {
    display: flex;
    justify-items: space-between;
    margin-left: 0.5rem;
    margin-bottom: 0;

    h2 {
      margin-bottom: 0;
    }
  }

  &__inner {
    max-width: 100%;
  }

  @media (min-width: $mobile-break-point) {
    &__inner {
      display: flex;
    }

    &__filters {
      flex: 1;
      padding: 1rem 1rem 0 0.5rem;
      min-width: 18rem;
    }

    &__table {
      flex: 8;
      overflow-x: auto;
      margin-top: -2.5rem;
    }
  }
}

// Table Styles
.header-row {
  background-color: $soft-gray;

  & > .md-table-head {
    font-size: 0.9rem;
  }
}

.body-row {
  & > .md-table-cell {
    border-top: 1px solid $soft-gray;
    padding: 0.5rem;
  }

  &:hover {
    background-color: $off-white;
    cursor: pointer;
  }
}

.warning-icon {
  color: $ruby;
}

.gray-row {
  background-color: $mid-gray;

  &:hover {
    background-color: $gray;
  }
}

.updated-icons {
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.icon-container {
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: $off-white;
    cursor: pointer;
  }
}

// Filter UI

.FilterAspect {
  margin-bottom: 1.5rem;
  padding: 1rem 0.75rem;
  border-radius: 0.25rem;

  .md-field {
    margin-bottom: 8px;
  }

  &:nth-of-type(odd) {
    background-color: #e4e4e4;
    border: 1px solid #ccc;
  }

  &:nth-of-type(even) {
    border: 1px solid #dadada;
  }
}

.FilterAspect__Title {
  border-bottom: 1px dotted #3d3d3d;
  color: #3d3d3d;
  font-weight: bold;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
}

.FilterAspect__Clear {
  color: gray;
}

.DateRange {
  .md-field {
    margin-bottom: 0.5rem;
  }
}

.Report__Filter__DateRange {
  overflow: hidden;
  margin-bottom: 1rem;

  .md-field {
    margin: 0;
  }
}
</style>
